<script setup>
// [turuta] Zona fija del panel de contacto, pegada a ContactInfo: la etapa, el
// asesor, la ventana de 24 h y el selector de etapa. Fuera del acordeon a
// proposito: mover de etapa es lo que mas veces al dia hace un asesor, y si
// estuviera dentro de una seccion el primer clic se gastaria en abrirla.
//
// Selector "B" (docs/10-plan-fork-ui.md): un boton para la etapa siguiente del
// embudo, que es el movimiento normal, y un desplegable para el resto.
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';
import { useLead } from './useLead';
import {
  HORAS_POR_DEFECTO,
  MAX_HORAS,
  MIN_HORAS,
  avisoDelRecordatorio,
  horasValidas,
} from './recordatorio';
import {
  correosDe,
  errorDeInvitados,
  destinoDeLaVisita,
  textoDelDestino,
  textoCopia,
  textoCalendario,
} from './calendario';
import {
  textoError,
  cuandoVisita,
  textoVisita,
  fechaInput,
  horaInput,
  isoDesdeInputs,
  textoIa,
  muestraIa,
} from './leadApp';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  contact: {
    type: Object,
    default: () => ({}),
  },
});

const { state, miId, cambiarEtapa, agendarVisita, quitarVisita, cambiarIa } =
  useLead(
    computed(() => props.conversationId),
    computed(() => props.contact)
  );

const lead = computed(() => (state.lead?.found ? state.lead : null));
const siguiente = computed(() => lead.value?.siguiente || null);
const otras = computed(() =>
  (lead.value?.siguientes || []).filter(s => s.code !== siguiente.value?.code)
);

const abierto = ref(false); // el desplegable "Mover a otra etapa"
const motivoPara = ref(null); // etapa que pide motivo, mientras se elige
const motivo = ref(null);
const nota = ref('');

// La visita: fecha, hora y direccion. Se pide al entrar en una etapa que la
// exige (Visita agendada) y se puede cambiar despues desde la ficha. Dos horas
// antes le llega al lead un recordatorio por WhatsApp (lo manda nuestra API).
const visitaPara = ref(null); // etapa que pide visita, mientras se rellena
const editandoVisita = ref(false); // cambiar la visita vigente
const vFecha = ref('');
const vHora = ref('');
const vDireccion = ref('');
const vInvitados = ref('');
// Cuantas horas antes le llega el recordatorio: lo elige quien agenda (21/10).
const vHorasRecordatorio = ref(HORAS_POR_DEFECTO);
const formularioVisita = computed(
  () => !!visitaPara.value || editandoVisita.value
);

// Google Calendar (docs/18 del repo crm-inmobi): la visita se pone tambien en
// el calendario del asesor asignado, o en el de quien agenda. Si ninguno lo
// tiene conectado no hay a quien invitar: en vez del campo, el enlace para
// conectar el propio. Sin configurar en el servidor, la ficha no dice nada.
const route = useRoute();
const destino = computed(() =>
  destinoDeLaVisita(lead.value?.calendario, miId.value)
);
const errorInvitados = computed(() => errorDeInvitados(vInvitados.value));
const ayudaInvitados = computed(() =>
  `${textoDelDestino(destino.value, {
    asesor: lead.value?.asesor,
    respaldo: lead.value?.calendario?.respaldo,
  })} A los invitados les llega la invitación por correo. ${textoCopia(
    lead.value?.calendario?.copia
  )}`.trim()
);
const rutaCalendario = computed(() => ({
  name: 'turuta_calendario',
  params: { accountId: route.params.accountId },
}));

function abrirVisita(etapa) {
  const actual = lead.value?.visita;
  visitaPara.value = etapa;
  editandoVisita.value = !etapa;
  vFecha.value = fechaInput(actual ? actual.scheduledAt : new Date());
  vHora.value = actual ? horaInput(actual.scheduledAt) : '';
  vDireccion.value = actual ? actual.address : '';
  vInvitados.value = (actual?.guests || []).join(', ');
  vHorasRecordatorio.value = actual?.reminderHours || HORAS_POR_DEFECTO;
  abierto.value = false;
}

// Que cualquier clic en el campo abra el selector de fecha u hora, no solo
// el icono de la derecha. showPicker solo existe en navegadores modernos y
// solo funciona dentro de un gesto del usuario; si no, el navegador hace lo
// suyo.
function abrirSelector(event) {
  const input = event.target;
  if (typeof input.showPicker !== 'function') return;
  try {
    input.showPicker();
  } catch (e) {
    // sin gesto de usuario o ya abierto: no pasa nada
  }
}

function cerrarVisita() {
  visitaPara.value = null;
  editandoVisita.value = false;
}

const visitaIso = computed(() => isoDesdeInputs(vFecha.value, vHora.value));
// Cuando le llega el recordatorio, o por que no (turuta/recordatorio.js).
const avisoRecordatorio = computed(() =>
  avisoDelRecordatorio({
    visitaIso: visitaIso.value,
    horas: vHorasRecordatorio.value,
    zona: lead.value?.recordatorio?.zonaHoraria,
    plantillaDelDia: !!lead.value?.recordatorio?.plantillaDelDia,
  })
);
const puedeGuardarVisita = computed(
  () =>
    !!visitaIso.value &&
    new Date(visitaIso.value) > new Date() &&
    !errorInvitados.value &&
    !avisoRecordatorio.value?.error &&
    !state.busy
);

async function guardarVisita() {
  if (!puedeGuardarVisita.value) return;
  const direccion = vDireccion.value.trim();
  const invitados = correosDe(vInvitados.value).correos;
  const horas = horasValidas(vHorasRecordatorio.value) ?? HORAS_POR_DEFECTO;
  const ok = visitaPara.value
    ? await cambiarEtapa(visitaPara.value.code, {
        visitAt: visitaIso.value,
        visitAddress: direccion,
        visitGuests: invitados,
        visitReminderHours: horas,
      })
    : await agendarVisita(visitaIso.value, direccion, invitados, horas);
  if (ok) cerrarVisita();
}

watch(
  () => state.conversationId,
  () => {
    abierto.value = false;
    motivoPara.value = null;
    cerrarVisita();
  }
);

function elegir(etapa) {
  abierto.value = false;
  if (etapa.requiereVisita) {
    abrirVisita(etapa);
    return;
  }
  if (etapa.requiereMotivo) {
    motivoPara.value = etapa;
    motivo.value = null;
    nota.value = '';
    return;
  }
  cambiarEtapa(etapa.code);
}

const faltaNota = computed(
  () => !!motivo.value?.requiresNote && !nota.value.trim()
);
const puedeConfirmar = computed(
  () => !!motivo.value && !faltaNota.value && !state.busy
);

async function confirmarMotivo() {
  if (!puedeConfirmar.value) return;
  const extra = { reason: motivo.value.code };
  if (motivo.value.requiresNote) extra.reasonNote = nota.value.trim();
  const ok = await cambiarEtapa(motivoPara.value.code, extra);
  if (ok) motivoPara.value = null;
}

// La IA atendiendo, en verde; en pausa, en gris (05/10: fila compacta con
// su boton al lado, en vez de un boton a todo lo ancho).
const iaAtendiendo = computed(() => lead.value?.ia?.estado === 'atendiendo');

// Perdido va en gris, no en rojo: en rojo llamaba demasiado la atencion en
// un panel que el asesor mira todo el dia.
const claseEtapa = computed(() => {
  if (!lead.value?.etapa?.terminal) return 'bg-n-blue-3 text-n-blue-11';
  return lead.value.perdida
    ? 'bg-n-slate-3 text-n-slate-11'
    : 'bg-n-teal-3 text-n-teal-11';
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <!-- [turuta] Textos en espanol a proposito: la ficha es nuestra y no pasa por el i18n de Chatwoot -->
  <div
    class="p-3 mx-2 mb-3 border rounded-xl border-n-weak bg-n-solid-1"
    data-turuta="lead-header"
  >
    <p v-if="state.error" class="text-xs text-n-slate-11">
      {{ textoError(state.error) }}
    </p>

    <div
      v-else-if="state.loading && !lead"
      class="flex flex-col gap-2 animate-pulse"
    >
      <div class="h-4 rounded bg-n-alpha-2" />
      <div class="w-2/3 h-4 rounded bg-n-alpha-2" />
    </div>

    <p
      v-else-if="state.lead && !state.lead.found"
      class="text-xs text-n-slate-11"
    >
      Esta conversacion todavia no tiene un lead asociado.
    </p>

    <template v-else-if="lead">
      <!-- La etapa. El asesor no va aqui (05/10): "Agente asignado", justo
           debajo, es el de Chatwoot y esta siempre al dia; el nuestro llega
           por webhook y, con un agente recien creado, tardaba en salir. -->
      <div class="flex items-center gap-2">
        <span
          class="px-2 py-0.5 text-xs font-medium rounded-md"
          :class="claseEtapa"
          :title="`Etapa: ${lead.etapa.name}`"
        >
          {{ lead.etapa.name }}
        </span>
      </div>
      <!-- La IA en este chat: es como sabe el asesor si sigue respondiendo
           ella o si ya es cosa suya, y por que (docs/12 del repo crm-inmobi).
           El boton va en la misma fila. -->
      <div
        v-if="muestraIa(lead.ia)"
        class="flex items-center justify-between gap-2 py-1 mt-2 rounded-lg ltr:pl-2 ltr:pr-1 rtl:pr-2 rtl:pl-1"
        :class="iaAtendiendo ? 'bg-n-teal-2' : 'bg-n-alpha-1'"
        data-turuta="lead-ia"
      >
        <span class="flex items-center min-w-0 gap-1.5 text-xs">
          <span
            class="flex-shrink-0 rounded-full size-2"
            :class="iaAtendiendo ? 'bg-n-teal-9' : 'bg-n-slate-8'"
          />
          <span
            class="font-semibold"
            :class="iaAtendiendo ? 'text-n-teal-11' : 'text-n-slate-12'"
          >
            IA
          </span>
          <span class="truncate text-n-slate-11" :title="textoIa(lead.ia)">
            {{ textoIa(lead.ia) }}
          </span>
        </span>
        <Button
          v-if="lead.ia.puedePausar"
          v-tooltip.top="'Pausar la IA en este chat'"
          label="Pausar"
          icon="i-lucide-pause"
          variant="ghost"
          color="slate"
          size="xs"
          class="flex-shrink-0"
          :disabled="state.busy"
          @click="cambiarIa(false)"
        />
        <Button
          v-else-if="lead.ia.puedeReanudar"
          v-tooltip.top="'Que la IA siga con este chat'"
          label="Reanudar"
          icon="i-lucide-play"
          variant="ghost"
          color="teal"
          size="xs"
          class="flex-shrink-0"
          :disabled="state.busy"
          @click="cambiarIa(true)"
        />
      </div>

      <div
        v-if="lead.perdida"
        class="px-2 py-1.5 mt-2 text-xs rounded-md bg-n-alpha-2 text-n-slate-11"
      >
        <div class="font-medium">{{ lead.perdida.name }}</div>
        <div v-if="lead.perdida.nota">{{ lead.perdida.nota }}</div>
      </div>

      <!-- La ventana de 24 h: abierta, una linea discreta; cerrada, un aviso. -->
      <div
        v-if="lead.ventana24h"
        class="flex items-center gap-1.5 mt-2 text-xs text-n-slate-11"
      >
        <span class="flex-shrink-0 i-lucide-clock size-3.5 text-n-teal-10" />
        <span>Ventana de 24 h abierta: responder es gratis.</span>
      </div>
      <div
        v-else
        class="flex items-start gap-1.5 px-2 py-1.5 mt-2 text-xs rounded-lg bg-n-amber-2 text-n-amber-11"
      >
        <span class="flex-shrink-0 i-lucide-alert-triangle size-3.5 mt-px" />
        <span>
          <b>Fuera de la ventana de 24 h.</b> Solo se puede escribir con una
          plantilla aprobada, y tiene coste.
        </span>
      </div>

      <div
        v-if="lead.visita && !formularioVisita"
        class="px-2 py-1.5 mt-2 text-xs rounded-md bg-n-alpha-2 text-n-slate-12"
      >
        <div class="flex items-center gap-1.5">
          <span
            class="flex-shrink-0 i-lucide-calendar-check size-3.5 text-n-slate-10"
          />
          <span class="font-medium">Visita</span>
          <span class="truncate">{{
            cuandoVisita(lead.visita.scheduledAt)
          }}</span>
        </div>
        <div v-if="lead.visita.address" class="truncate text-n-slate-11">
          {{ lead.visita.address }}
        </div>
        <div class="text-n-slate-11">{{ textoVisita(lead.visita) }}</div>
        <!-- Google Calendar: en el de quien esta la visita, o por que no. -->
        <div
          v-if="lead.visita.calendario"
          class="flex items-start gap-1.5 mt-1"
          :class="
            lead.visita.calendario.estado === 'error'
              ? 'text-n-amber-11'
              : 'text-n-slate-11'
          "
          data-turuta="visita-calendario"
        >
          <span
            class="flex-shrink-0 size-3.5 mt-px"
            :class="
              lead.visita.calendario.estado === 'ok'
                ? 'i-lucide-calendar-check text-n-teal-10'
                : 'i-lucide-calendar-x'
            "
          />
          <span>
            {{ textoCalendario(lead.visita.calendario) }}
            <router-link
              v-if="lead.visita.calendario.estado === 'sin_conectar'"
              :to="rutaCalendario"
              class="text-n-blue-11 hover:underline"
            >
              Conectar el mío
            </router-link>
          </span>
        </div>
        <div
          v-if="lead.visita.guests && lead.visita.guests.length"
          class="truncate text-n-slate-11"
          :title="lead.visita.guests.join(', ')"
        >
          Invitados: {{ lead.visita.guests.join(', ') }}
        </div>
        <div class="flex gap-3 mt-1">
          <button
            type="button"
            class="text-n-blue-11 hover:underline"
            :disabled="state.busy"
            @click="abrirVisita(null)"
          >
            Cambiar
          </button>
          <button
            type="button"
            class="text-n-slate-11 hover:underline"
            :disabled="state.busy"
            @click="quitarVisita()"
          >
            Quitar
          </button>
        </div>
      </div>

      <div
        v-if="motivoPara"
        class="p-2 mt-3 border rounded-lg border-n-weak bg-n-alpha-1"
      >
        <div class="text-sm font-medium text-n-slate-12">
          Marcar como {{ motivoPara.name.toLowerCase() }}
        </div>
        <div class="mt-0.5 text-xs text-n-slate-11">
          El motivo es obligatorio: sin el, los informes de perdida no valen
          nada.
        </div>
        <div class="flex flex-col gap-1 mt-2">
          <label
            v-for="m in lead.motivosPerdida"
            :key="m.code"
            class="flex items-center gap-2 text-sm cursor-pointer text-n-slate-12"
          >
            <input
              v-model="motivo"
              type="radio"
              name="turuta-motivo"
              :value="m"
            />
            <span>{{ m.name }}</span>
          </label>
        </div>
        <textarea
          v-if="motivo && motivo.requiresNote"
          v-model="nota"
          rows="2"
          placeholder="Cuenta que paso"
          class="w-full px-2 py-1 mt-2 text-sm border rounded-md border-n-weak bg-n-background text-n-slate-12"
        />
        <div class="flex justify-end gap-2 mt-2">
          <Button
            label="Cancelar"
            variant="ghost"
            color="slate"
            size="sm"
            @click="motivoPara = null"
          />
          <Button
            :label="`Marcar como ${motivoPara.name.toLowerCase()}`"
            variant="solid"
            color="blue"
            size="sm"
            :disabled="!puedeConfirmar"
            :is-loading="state.busy"
            @click="confirmarMotivo"
          />
        </div>
      </div>

      <div
        v-else-if="formularioVisita"
        class="p-3 mt-3 border rounded-xl border-n-weak bg-n-solid-1"
      >
        <div class="text-sm font-medium text-n-slate-12">
          {{ visitaPara ? 'Agendar la visita' : 'Cambiar la visita' }}
        </div>
        <p class="mt-0.5 text-xs text-n-slate-11">
          Antes de la visita le llega al lead un recordatorio por WhatsApp.
        </p>
        <label class="block mt-3 text-xs font-medium text-n-slate-11">
          Fecha
          <input
            v-model="vFecha"
            type="date"
            :min="fechaInput(new Date())"
            class="turuta-input"
            @click="abrirSelector"
          />
        </label>
        <label class="block mt-2 text-xs font-medium text-n-slate-11">
          Hora
          <input
            v-model="vHora"
            type="time"
            class="turuta-input"
            @click="abrirSelector"
          />
        </label>
        <label class="block mt-2 text-xs font-medium text-n-slate-11">
          Recordatorio por WhatsApp
          <span class="flex items-center gap-2 mt-1">
            <input
              v-model.number="vHorasRecordatorio"
              type="number"
              inputmode="numeric"
              :min="MIN_HORAS"
              :max="MAX_HORAS"
              step="1"
              class="turuta-input !w-20 !mt-0"
              data-turuta="visita-horas"
            />
            <span class="font-normal text-n-slate-11">horas antes</span>
          </span>
          <span
            v-if="avisoRecordatorio"
            class="block mt-1 font-normal"
            :class="
              avisoRecordatorio.error ? 'text-n-ruby-11' : 'text-n-slate-10'
            "
            data-turuta="visita-horas-aviso"
          >
            {{ avisoRecordatorio.texto }}
          </span>
        </label>
        <label class="block mt-2 text-xs font-medium text-n-slate-11">
          Direccion
          <span class="font-normal text-n-slate-10">(opcional)</span>
          <input
            v-model="vDireccion"
            type="text"
            placeholder="Av. Larco 1234, Miraflores"
            class="turuta-input"
          />
        </label>
        <template v-if="lead.calendario">
          <label
            v-if="destino"
            class="block mt-2 text-xs font-medium text-n-slate-11"
          >
            Invitar a otros correos
            <span class="font-normal text-n-slate-10">(opcional)</span>
            <input
              v-model="vInvitados"
              type="text"
              inputmode="email"
              autocomplete="off"
              placeholder="ana@gmail.com, luis@empresa.com"
              class="turuta-input"
              data-turuta="visita-invitados"
            />
            <span
              class="block mt-1 font-normal"
              :class="errorInvitados ? 'text-n-ruby-11' : 'text-n-slate-10'"
            >
              {{ errorInvitados || ayudaInvitados }}
            </span>
          </label>
          <p v-else class="mt-2 mb-0 text-xs text-n-slate-10">
            Para ponerla en Google Calendar e invitar a otros por correo,
            <router-link
              :to="rutaCalendario"
              class="text-n-blue-11 hover:underline"
            >
              conecta tu calendario
            </router-link>
          </p>
        </template>
        <div class="flex justify-end gap-2 mt-3">
          <Button
            label="Cancelar"
            variant="ghost"
            color="slate"
            size="sm"
            @click="cerrarVisita"
          />
          <Button
            :label="visitaPara ? 'Agendar' : 'Guardar'"
            variant="solid"
            color="blue"
            size="sm"
            :disabled="!puedeGuardarVisita"
            :is-loading="state.busy"
            @click="guardarVisita"
          />
        </div>
      </div>

      <div
        v-else-if="lead.siguientes.length"
        class="flex flex-col gap-1.5 mt-3"
      >
        <Button
          v-if="siguiente"
          :label="`Mover a ${siguiente.name}`"
          icon="i-lucide-arrow-right"
          variant="solid"
          color="blue"
          size="sm"
          class="w-full"
          :disabled="state.busy"
          :is-loading="state.busy"
          @click="elegir(siguiente)"
        />
        <Button
          v-if="otras.length"
          :label="abierto ? 'Cerrar' : 'Mover a otra etapa'"
          :icon="abierto ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
          trailing-icon
          variant="ghost"
          color="slate"
          size="sm"
          class="w-full"
          :disabled="state.busy"
          @click="abierto = !abierto"
        />
        <div
          v-if="abierto"
          class="flex flex-col gap-1 p-1 border rounded-lg border-n-weak bg-n-alpha-1"
        >
          <button
            v-for="s in otras"
            :key="s.code"
            type="button"
            class="flex items-center justify-between w-full px-2 py-1.5 text-sm text-left rounded-md text-n-slate-12 hover:bg-n-alpha-2"
            :disabled="state.busy"
            @click="elegir(s)"
          >
            <span>{{ s.name }}</span>
            <span v-if="s.requiereMotivo" class="text-xs text-n-slate-10">
              pide motivo
            </span>
          </button>
        </div>
      </div>

      <p v-if="state.aviso" class="mt-2 text-xs text-n-ruby-11">
        {{ state.aviso }}
      </p>
    </template>
  </div>
</template>

<style scoped>
/* [turuta] Campos del formulario de visita, con los mismos tokens que los
   inputs de Chatwoot. */
.turuta-input {
  @apply block w-full h-9 px-3 mt-1 text-sm font-normal rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12 outline-none hover:border-n-slate-6 focus:border-n-strong;
}
input[type='date'].turuta-input::-webkit-calendar-picker-indicator,
input[type='time'].turuta-input::-webkit-calendar-picker-indicator {
  @apply opacity-60 cursor-pointer;
}
</style>
